import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, formatTanggal } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { ClipboardList, Eye, Camera, MapPin, ExternalLink } from "lucide-react";
import { formatJam } from "@/lib/geo";

const AVATARS = ["bg-sky-100 text-sky-700","bg-amber-100 text-amber-700","bg-emerald-100 text-emerald-700","bg-rose-100 text-rose-700","bg-teal-100 text-teal-700","bg-indigo-100 text-indigo-700"];
function avatarColor(s) { let h=0; for (let i=0;i<(s||"").length;i++) h=(h*31+s.charCodeAt(i))%9973; return AVATARS[h%AVATARS.length]; }
function initials(s) { return (s||"?").slice(0,2).toUpperCase(); }

export default function AttendanceRecapPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ start: "", end: "" });
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter.start) params.start = filter.start;
      if (filter.end) params.end = filter.end;
      const { data } = await api.get("/attendance", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [filter.start, filter.end]);

  useEffect(() => { load(); }, [load]);

  async function openDetail(id) {
    try {
      const { data } = await api.get(`/attendance/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="space-y-6" data-testid="attendance-recap-page">
      <PageHeader
        overline="Administrasi"
        title="Rekap Absensi"
        description="Riwayat kedatangan dan kepulangan seluruh karyawan."
        icon={ClipboardList}
        tone="success"
      />

      <Card className="p-5 border-border shadow-none">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label>Dari tanggal</Label>
            <DatePicker value={filter.start} onChange={(v) => setFilter({ ...filter, start: v })} testId="recap-start" placeholder="Tanggal awal" allowClear />
          </div>
          <div className="space-y-1">
            <Label>Sampai tanggal</Label>
            <DatePicker value={filter.end} onChange={(v) => setFilter({ ...filter, end: v })} testId="recap-end" placeholder="Tanggal akhir" allowClear />
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden border-border shadow-none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tanggal</TableHead>
              <TableHead>Karyawan</TableHead>
              <TableHead>Datang</TableHead>
              <TableHead>Pulang</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-ink-500">Belum ada data absensi.</TableCell></TableRow>
            )}
            {items.map((a) => (
              <TableRow key={a.id} data-testid={`row-att-${a.id}`}>
                <TableCell className="whitespace-nowrap">{formatTanggal(a.date)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className={`grid h-8 w-8 place-items-center rounded-full text-xs font-semibold ${avatarColor(a.user_username)}`}>
                      {initials(a.user_name || a.user_username)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-ink-900">{a.user_name || a.user_username}</div>
                      <div className="text-xs text-ink-500">@{a.user_username}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-mono text-sm">{formatJam(a.check_in_at)}</div>
                  {a.check_in_late && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Terlambat</Badge>}
                </TableCell>
                <TableCell>
                  <div className="font-mono text-sm">{formatJam(a.check_out_at)}</div>
                  {a.check_out_early && <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">Pulang awal</Badge>}
                </TableCell>
                <TableCell>
                  {a.check_out_at ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Selesai</Badge>
                  ) : a.check_in_at ? (
                    <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">Hadir</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">—</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => openDetail(a.id)} data-testid={`view-att-${a.id}`}>
                    <Eye className="mr-1 h-3 w-3" /> Lihat
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detail Absensi</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className={`grid h-10 w-10 place-items-center rounded-full text-xs font-semibold ${avatarColor(detail.user_username)}`}>
                  {initials(detail.user_name)}
                </div>
                <div>
                  <div className="font-medium text-ink-900">{detail.user_name}</div>
                  <div className="text-xs text-ink-500">@{detail.user_username} · {formatTanggal(detail.date)}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Check-in */}
                <div className="rounded-md border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-3">
                  <p className="overline text-emerald-700">Datang</p>
                  <p className="mt-1 font-mono text-lg font-semibold text-emerald-900">{formatJam(detail.check_in_at)}</p>
                  {detail.check_in_photo ? (
                    <img src={detail.check_in_photo} alt="checkin" className="mt-2 rounded-md w-full object-cover" />
                  ) : detail.check_in_at ? (
                    <div className="mt-2 rounded-md border border-dashed border-emerald-200 bg-white p-4 text-center text-xs text-ink-500">
                      Foto telah dihapus otomatis (kebijakan retensi).
                    </div>
                  ) : null}
                  {detail.check_in_lat && (
                    <a href={`https://www.google.com/maps?q=${detail.check_in_lat},${detail.check_in_lng}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-brand hover:underline">
                      <MapPin className="h-3 w-3" /> {detail.check_in_lat.toFixed(5)}, {detail.check_in_lng.toFixed(5)} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  <div className="mt-1 text-xs text-ink-500">±{Math.round(detail.check_in_distance_m || 0)} m dari kantor</div>
                </div>
                {/* Check-out */}
                <div className="rounded-md border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-3">
                  <p className="overline text-amber-700">Pulang</p>
                  <p className="mt-1 font-mono text-lg font-semibold text-amber-900">{formatJam(detail.check_out_at)}</p>
                  {detail.check_out_photo ? (
                    <img src={detail.check_out_photo} alt="checkout" className="mt-2 rounded-md w-full object-cover" />
                  ) : detail.check_out_at ? (
                    <div className="mt-2 rounded-md border border-dashed border-amber-200 bg-white p-4 text-center text-xs text-ink-500">
                      Foto telah dihapus otomatis (kebijakan retensi).
                    </div>
                  ) : null}
                  {detail.check_out_lat && (
                    <a href={`https://www.google.com/maps?q=${detail.check_out_lat},${detail.check_out_lng}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-brand hover:underline">
                      <MapPin className="h-3 w-3" /> {detail.check_out_lat.toFixed(5)}, {detail.check_out_lng.toFixed(5)} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {detail.check_out_at && (
                    <div className="mt-1 text-xs text-ink-500">±{Math.round(detail.check_out_distance_m || 0)} m dari kantor</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
